import React, { useState } from 'react';
import type { Socket } from 'socket.io-client';

interface AdminPanelSanctumTabProps {
  socket?: Socket | null;
  playerId?: string;
  isBusy: boolean;
  onStateChange: (state: { isBusy: boolean; lastMessage: string | null }) => void;
  setLoadingOverlayVisible?: (visible: boolean) => void;
  setLoadingOverlayMessage?: (message: string) => void;
  setIsInstanceEntry?: (isInstance: boolean) => void;
}

export const AdminPanelSanctumTab: React.FC<AdminPanelSanctumTabProps> = ({
  socket,
  playerId,
  isBusy,
  onStateChange,
  setLoadingOverlayVisible,
  setLoadingOverlayMessage,
  setIsInstanceEntry,
}) => {
  const [sanctumGrade, setSanctumGrade] = useState<'A' | 'B' | 'C' | 'D'>('A');
  const [sanctumRoomCount, setSanctumRoomCount] = useState<number>(10);

  const createAndEnterSanctum = (): void => {
    if (!socket) {
      onStateChange({ isBusy: false, lastMessage: 'Socket not available' });
      return;
    }
    
    // Show loading overlay immediately
    if (setIsInstanceEntry && setLoadingOverlayMessage && setLoadingOverlayVisible) {
      setIsInstanceEntry(true);
      setLoadingOverlayMessage('Creating Sanctum...');
      setLoadingOverlayVisible(true);
      setTimeout(() => setLoadingOverlayVisible(false), 5000);
    }
    
    onStateChange({ isBusy: true, lastMessage: 'Creating sanctum...' });
    socket.emit('sanctum:create_and_enter', {
      playerId: playerId || socket.id,
      config: {
        grade: sanctumGrade,
        roomCount: sanctumRoomCount,
      },
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {/* Create Sanctum */}
      <div style={{ padding: '6px', border: '1px solid #4a5568', backgroundColor: '#111827' }}>
        <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 10 }}>🏛️ Create Sanctum</div>
        
        <div style={{ marginBottom: 4, display: 'flex', gap: 2 }}>
          <select
            value={sanctumGrade}
            onChange={(e) => setSanctumGrade(e.target.value as 'A' | 'B' | 'C' | 'D')}
            disabled={isBusy}
            style={{
              flex: 1,
              padding: '2px 4px',
              fontSize: 9,
              backgroundColor: '#2d3748',
              color: '#edf2f7',
              border: '1px solid #4a5568',
              borderRadius: 0,
              cursor: isBusy ? 'default' : 'pointer',
            }}
          >
            <option value="A">Grade A</option>
            <option value="B">Grade B</option>
            <option value="C">Grade C</option>
            <option value="D">Grade D</option>
          </select>

          <input
            type="number"
            min="8"
            max="12"
            value={sanctumRoomCount}
            onChange={(e) => setSanctumRoomCount(Math.max(8, Math.min(12, parseInt(e.target.value) || 10)))}
            disabled={isBusy}
            style={{
              width: 50,
              padding: '2px 4px',
              fontSize: 9,
              backgroundColor: '#2d3748',
              color: '#edf2f7',
              border: '1px solid #4a5568',
              borderRadius: 0,
            }}
          />
        </div>

        <button
          type="button"
          onClick={createAndEnterSanctum}
          disabled={isBusy}
          style={{
            width: '100%',
            padding: '2px 6px',
            fontSize: 10,
            backgroundColor: '#6b46c1',
            color: '#e9d5ff',
            border: '1px solid #a78bfa',
            borderRadius: 0,
            cursor: isBusy ? 'default' : 'pointer',
            fontWeight: 'bold',
          }}
        >
          {isBusy ? 'Creating...' : '🏛️ Create & Enter'}
        </button>
      </div>

      <div style={{ color: '#a0aec0', fontSize: 9, padding: '4px', textAlign: 'center' }}>
        💡 Creates a procedurally generated sanctum with the selected grade and room count
      </div>
    </div>
  );
};
